import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  ArrowRight,
  BarChart3,
  Building2,
  ClipboardCheck,
  MessageSquareText,
  Mic,
  ShieldCheck,
  Sparkles,
  Stethoscope,
} from 'lucide-react';
import { marketingLogos, marketingPalette, marketingSymbols, patientSlides } from '../content/marketing';

const headingFontFamily = '"Sora", "Manrope", sans-serif';
const bodyFontFamily = '"Manrope", "Segoe UI", sans-serif';
const palette = marketingPalette;

const featurePillars = [
  {
    icon: Stethoscope,
    title: 'High-fidelity virtual patients',
    copy:
      'Practise with animated patients in a realistic consultation room rather than a static text interface or scripted peer exercise.',
  },
  {
    icon: ClipboardCheck,
    title: 'Structured, exam-style feedback',
    copy:
      'See what you covered well, what you missed, and how to improve the next time you attempt the case.',
  },
  {
    icon: Building2,
    title: 'Built for universities too',
    copy:
      'Medical schools can create their own cases, marking criteria, investigations, and analytics while keeping academic control of the content.',
  },
];

const audiencePanels = [
  {
    title: 'For students',
    eyebrow: 'Independent practice that still feels purposeful',
    points: [
      'Rehearse consultations without needing friends or family to read a script.',
      'Focus on the patient interaction instead of trying to self-mark in real time.',
      'Repeat cases until the consultation structure feels natural before exams and placements.',
    ],
  },
  {
    title: 'For universities',
    eyebrow: 'Curriculum control without losing scalability',
    points: [
      'Create validated cases, patient narratives, mark schemes, and examiner questions.',
      'Keep institution-owned content and data inside the university environment.',
      'Use analytics to spot weak areas, support cohorts, and target teaching more effectively.',
    ],
  },
];

const practiceLoop = [
  {
    icon: Mic,
    step: '01',
    title: 'Run the consultation',
    copy: 'Speak to a virtual patient as you would in a real history-taking station.',
  },
  {
    icon: MessageSquareText,
    step: '02',
    title: 'Review the feedback',
    copy: 'Receive structured comments that show what you asked well and what you missed.',
  },
  {
    icon: BarChart3,
    step: '03',
    title: 'Improve the next attempt',
    copy: 'Practise again with clearer priorities, stronger structure, and less uncertainty.',
  },
];

function HomePage() {
  const isAuthenticated = Boolean(localStorage.getItem('token'));
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    patientSlides.forEach((slide) => {
      const image = new window.Image();
      image.src = slide.src;
    });
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % patientSlides.length);
    }, 3400);

    return () => window.clearInterval(timer);
  }, []);

  return (
    <Box
      sx={{
        bgcolor: palette.heroBlueMist,
        color: palette.ink,
        fontFamily: bodyFontFamily,
        '@keyframes vmRiseIn': {
          from: {
            opacity: 0,
            transform: 'translateY(28px)',
          },
          to: {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
        '@keyframes vmFloat': {
          '0%': {
            transform: 'translateY(0px)',
          },
          '50%': {
            transform: 'translateY(-10px)',
          },
          '100%': {
            transform: 'translateY(0px)',
          },
        },
      }}
    >
      <Box
        component="section"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          background:
            `radial-gradient(circle at top left, ${alpha(palette.heroBlue, 0.26)}, transparent 34%), linear-gradient(180deg, #eaf7ff 0%, #f8fcff 58%, #edf8fe 100%)`,
          pt: { xs: 4, md: 6 },
          pb: { xs: 7, md: 10 },
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 'auto auto 12% -6%',
            width: { xs: 180, md: 260 },
            height: { xs: 180, md: 260 },
            borderRadius: '50%',
            background: alpha(palette.heroBlue, 0.14),
            filter: 'blur(4px)',
          },
        }}
      >
        <Box
          component="img"
          src={marketingSymbols.silhouette}
          alt=""
          aria-hidden
          sx={{
            display: { xs: 'none', lg: 'block' },
            position: 'absolute',
            top: 54,
            right: '11%',
            width: { lg: 240, xl: 300 },
            maxWidth: '22vw',
            opacity: 0.22,
            zIndex: 0,
            pointerEvents: 'none',
          }}
        />
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 3, md: 4 } }}>
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
            <Grid item xs={12} lg={6}>
              <Box
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: { xs: '28px', md: '36px' },
                  p: { xs: 3, sm: 4, md: 5 },
                  background:
                    'linear-gradient(155deg, rgba(18, 61, 103, 0.98) 0%, rgba(24, 84, 132, 0.96) 58%, rgba(41, 145, 194, 0.92) 100%)',
                  color: palette.white,
                  boxShadow: '0 36px 90px rgba(18, 61, 103, 0.22)',
                  animation: 'vmRiseIn 720ms ease-out both',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 'auto -12% -20% auto',
                    width: 240,
                    height: 240,
                    borderRadius: '50%',
                    background: alpha('#ffffff', 0.12),
                    filter: 'blur(8px)',
                  },
                }}
              >
                <Chip
                  label="AI consultation practice for medical education"
                  sx={{
                    mb: 3,
                    width: 'fit-content',
                    bgcolor: alpha('#ffffff', 0.12),
                    color: '#f7fbfa',
                    border: `1px solid ${alpha('#ffffff', 0.16)}`,
                    fontWeight: 700,
                    letterSpacing: '0.02em',
                    fontFamily: bodyFontFamily,
                  }}
                />
                <Box
                  component="img"
                  src={marketingLogos.lockupLight}
                  alt="Virtual Medic"
                  sx={{
                    width: { xs: '100%', sm: '82%', md: '74%' },
                    maxWidth: 440,
                    mb: 4,
                  }}
                />
                <Typography
                  variant="h1"
                  sx={{
                    mb: 2.5,
                    fontFamily: headingFontFamily,
                    fontSize: { xs: '2.35rem', sm: '3rem', md: '3.5rem' },
                    lineHeight: 1.04,
                    letterSpacing: '-0.03em',
                    maxWidth: 620,
                  }}
                >
                  Practise full consultations with virtual patients.
                </Typography>
                <Typography
                  sx={{
                    mb: 4,
                    maxWidth: 620,
                    color: alpha('#f9fcfb', 0.82),
                    fontSize: { xs: '1rem', sm: '1.08rem', md: '1.12rem' },
                    lineHeight: 1.75,
                  }}
                >
                  Virtual Medic helps medical students rehearse clinical communication independently,
                  then review structured feedback that shows exactly how to improve. Universities can
                  build their own cases, marking criteria, and analytics around the same workflow.
                </Typography>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{ mb: 2.5, alignItems: { sm: 'center' } }}
                >
                  <Button
                    component={Link}
                    to={isAuthenticated ? '/dashboard' : '/signup'}
                    endIcon={<ArrowRight size={18} />}
                    variant="contained"
                    sx={{
                      minHeight: 52,
                      px: 3,
                      bgcolor: palette.heroBlue,
                      color: palette.heroBlueDeep,
                      textTransform: 'none',
                      fontWeight: 800,
                      fontSize: '0.98rem',
                      fontFamily: bodyFontFamily,
                      borderRadius: '999px',
                      boxShadow: 'none',
                      '&:hover': {
                        bgcolor: '#55c7ee',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {isAuthenticated ? 'Open dashboard' : 'Get started'}
                  </Button>
                  <Button
                    component={Link}
                    to="/downloads"
                    variant="outlined"
                    sx={{
                      minHeight: 52,
                      px: 3,
                      color: '#f9fcfb',
                      textTransform: 'none',
                      fontWeight: 700,
                      fontFamily: bodyFontFamily,
                      borderRadius: '999px',
                      borderColor: alpha('#ffffff', 0.26),
                      '&:hover': {
                        borderColor: alpha('#ffffff', 0.48),
                        bgcolor: alpha('#ffffff', 0.05),
                      },
                    }}
                  >
                    Download app
                  </Button>
                </Stack>
                <Button
                  component={Link}
                  to="/about"
                  endIcon={<ArrowRight size={18} />}
                  sx={{
                    mb: 4,
                    px: 0,
                    alignSelf: 'flex-start',
                    color: palette.heroBlueSoft,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontFamily: bodyFontFamily,
                    '&:hover': {
                      bgcolor: 'transparent',
                      color: '#e7faff',
                    },
                  }}
                >
                  Read the story behind Virtual Medic
                </Button>
                <Stack direction="row" flexWrap="wrap" useFlexGap gap={1.25} sx={{ maxWidth: 660 }}>
                  {['Unlimited repeat practice', 'Structured feedback', 'University-ready control'].map(
                    (label) => (
                      <Box
                        key={label}
                        sx={{
                          px: 1.5,
                          py: 1,
                          borderRadius: '999px',
                          border: `1px solid ${alpha('#ffffff', 0.14)}`,
                          bgcolor: alpha('#ffffff', 0.07),
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.88rem',
                            fontWeight: 700,
                            color: alpha('#f9fcfb', 0.88),
                          }}
                        >
                          {label}
                        </Typography>
                      </Box>
                    ),
                  )}
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Box
                sx={{
                  position: 'relative',
                  animation: 'vmRiseIn 880ms 120ms ease-out both',
                }}
              >
                <Box
                  sx={{
                    display: { xs: 'none', md: 'block' },
                    position: 'absolute',
                    top: -26,
                    right: 18,
                    px: 2,
                    py: 1.5,
                    borderRadius: '20px',
                    bgcolor: alpha('#ffffff', 0.84),
                    border: `1px solid ${alpha(palette.ink, 0.08)}`,
                    boxShadow: '0 22px 50px rgba(18, 61, 103, 0.10)',
                    animation: 'vmFloat 6s ease-in-out infinite',
                    zIndex: 2,
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <Sparkles size={18} color={palette.heroBlueMid} />
                    <Typography sx={{ fontWeight: 800, fontSize: '0.92rem' }}>
                      Diverse patient showcase
                    </Typography>
                  </Stack>
                </Box>
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: { xs: '28px', md: '34px' },
                    p: { xs: 1.5, sm: 2 },
                    bgcolor: alpha('#ffffff', 0.82),
                    border: `1px solid ${alpha(palette.ink, 0.08)}`,
                    backdropFilter: 'blur(16px)',
                    boxShadow: '0 28px 70px rgba(18, 61, 103, 0.14)',
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      px: { xs: 1.5, sm: 2 },
                      py: 1.5,
                    }}
                  >
                    {['#8adfff', '#5dcbf1', '#2eaad8'].map((swatch) => (
                      <Box
                        key={swatch}
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: swatch,
                        }}
                      />
                    ))}
                    <Typography sx={{ ml: 1, fontWeight: 800, fontSize: '0.92rem', color: palette.inkSoft }}>
                      Consultation showcase
                    </Typography>
                  </Stack>
                  <Box
                    sx={{
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: { xs: '22px', md: '28px' },
                      aspectRatio: { xs: '4 / 5', sm: '16 / 10' },
                      bgcolor: palette.heroBlueSurface,
                    }}
                  >
                    {patientSlides.map((slide, index) => (
                      <Box
                        key={slide.src}
                        component="img"
                        src={slide.src}
                        alt={slide.alt}
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          objectPosition: 'center',
                          opacity: index === activeSlide ? 1 : 0,
                          transform: index === activeSlide ? 'scale(1)' : 'scale(1.04)',
                          transition: 'opacity 900ms ease, transform 4600ms ease',
                          filter: index === activeSlide ? 'none' : 'brightness(0.95)',
                        }}
                      />
                    ))}
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background:
                          'linear-gradient(180deg, rgba(13, 45, 78, 0.08) 0%, rgba(13, 45, 78, 0) 42%, rgba(13, 45, 78, 0.72) 100%)',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        p: { xs: 2, sm: 2.5, md: 3 },
                        color: palette.white,
                      }}
                    >
                      <Typography
                        sx={{
                          mb: 0.75,
                          fontFamily: headingFontFamily,
                          fontSize: { xs: '1.35rem', sm: '1.5rem' },
                          lineHeight: 1.1,
                        }}
                      >
                        Diverse patients. Broader representation.
                      </Typography>
                      <Typography
                        sx={{
                          maxWidth: 420,
                          fontSize: { xs: '0.95rem', sm: '1rem' },
                          color: alpha(palette.white, 0.82),
                          lineHeight: 1.6,
                        }}
                      >
                        A more diverse patient range broadens representation across ages,
                        ethnicities, and presentation styles.
                      </Typography>
                      <Typography
                        sx={{
                          mt: 1.5,
                          fontWeight: 800,
                          fontSize: '0.85rem',
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          color: alpha(palette.white, 0.72),
                        }}
                      >
                        Patient {activeSlide + 1} of {patientSlides.length}
                      </Typography>
                    </Box>
                  </Box>
                  <Grid container spacing={1.5} sx={{ mt: 1.5 }}>
                    {[
                      'Shared consultation environment',
                      'Illustrative patient diversity',
                      'Optimised for desktop and mobile',
                    ].map((item) => (
                      <Grid item xs={12} sm={4} key={item}>
                        <Box
                          sx={{
                            height: '100%',
                            borderRadius: '20px',
                            px: 1.75,
                            py: 1.5,
                            bgcolor: alpha('#ffffff', 0.74),
                            border: `1px solid ${alpha(palette.ink, 0.06)}`,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '0.86rem',
                              fontWeight: 800,
                              lineHeight: 1.45,
                              color: palette.ink,
                            }}
                          >
                            {item}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Box
        component="section"
        sx={{
          py: { xs: 7, md: 9 },
          background: 'linear-gradient(180deg, #f8fcff 0%, #ffffff 100%)',
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Box sx={{ maxWidth: 760, mb: 4.5 }}>
            <Typography
              sx={{
                mb: 1.25,
                color: palette.heroBlueMid,
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                fontSize: '0.82rem',
              }}
            >
              What Virtual Medic actually does
            </Typography>
            <Typography
              variant="h2"
              sx={{
                mb: 2,
                fontFamily: headingFontFamily,
                fontSize: { xs: '2rem', sm: '2.45rem', md: '3rem' },
                lineHeight: 1.08,
                letterSpacing: '-0.03em',
              }}
            >
              A realistic consultation practice platform for students and institutions.
            </Typography>
            <Typography
              sx={{
                maxWidth: 680,
                fontSize: { xs: '1rem', md: '1.08rem' },
                lineHeight: 1.75,
                color: palette.inkSoft,
              }}
            >
              Virtual Medic is designed to close the feedback gap in consultation practice. It gives
              students repeated, low-pressure rehearsal and gives medical schools a framework they can
              actually shape around their curriculum.
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {featurePillars.map((pillar) => {
              const Icon = pillar.icon;

              return (
                <Grid item xs={12} md={4} key={pillar.title}>
                  <Box
                    sx={{
                      height: '100%',
                      borderRadius: '28px',
                      p: { xs: 3, md: 3.5 },
                      border: `1px solid ${alpha(palette.ink, 0.08)}`,
                      bgcolor: '#ffffff',
                      boxShadow: '0 18px 48px rgba(18, 61, 103, 0.06)',
                    }}
                  >
                    <Box
                      sx={{
                        mb: 2.5,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 54,
                        height: 54,
                        borderRadius: '18px',
                        bgcolor: alpha(palette.heroBlue, 0.14),
                        color: palette.heroBlueMid,
                      }}
                    >
                      <Icon size={26} strokeWidth={1.9} />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        mb: 1.5,
                        fontFamily: headingFontFamily,
                        fontSize: '1.45rem',
                        lineHeight: 1.18,
                      }}
                    >
                      {pillar.title}
                    </Typography>
                    <Typography sx={{ color: palette.inkSoft, lineHeight: 1.72 }}>
                      {pillar.copy}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 7, md: 9 }, background: '#eef8fe' }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Box sx={{ maxWidth: 760, mb: 4.5 }}>
            <Typography
              sx={{
                mb: 1.25,
                color: palette.heroBlueMid,
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                fontSize: '0.82rem',
              }}
            >
              Built for both sides of training
            </Typography>
            <Typography
              variant="h2"
              sx={{
                mb: 2,
                fontFamily: headingFontFamily,
                fontSize: { xs: '2rem', sm: '2.45rem', md: '3rem' },
                lineHeight: 1.08,
                letterSpacing: '-0.03em',
              }}
            >
              Welcoming for new learners. Structured enough for serious teaching.
            </Typography>
            <Typography
              sx={{
                maxWidth: 700,
                fontSize: { xs: '1rem', md: '1.08rem' },
                lineHeight: 1.75,
                color: palette.inkSoft,
              }}
            >
              The platform is designed to reduce friction for students while still giving educators
              meaningful control over cases, criteria, and curriculum alignment.
            </Typography>
          </Box>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {audiencePanels.map((panel) => (
              <Grid item xs={12} md={6} key={panel.title}>
                <Box
                  sx={{
                    height: '100%',
                    borderRadius: '30px',
                    p: { xs: 3, md: 3.5 },
                    border: `1px solid ${alpha(palette.ink, 0.08)}`,
                    bgcolor: '#ffffff',
                  }}
                >
                  <Typography
                    sx={{
                      mb: 1,
                      color: palette.heroBlueMid,
                      fontWeight: 800,
                      fontSize: '0.82rem',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {panel.eyebrow}
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      mb: 2.25,
                      fontFamily: headingFontFamily,
                      fontSize: '1.6rem',
                      lineHeight: 1.16,
                    }}
                  >
                    {panel.title}
                  </Typography>
                  <Stack component="ul" spacing={1.5} sx={{ m: 0, p: 0, listStyle: 'none' }}>
                    {panel.points.map((point) => (
                      <Stack
                        key={point}
                        component="li"
                        direction="row"
                        spacing={1.25}
                        alignItems="flex-start"
                      >
                        <ShieldCheck size={18} color={palette.heroBlueMid} style={{ marginTop: 3, flexShrink: 0 }} />
                        <Typography sx={{ color: palette.inkSoft, lineHeight: 1.72 }}>{point}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Grid container spacing={3}>
            {practiceLoop.map((item) => {
              const Icon = item.icon;

              return (
                <Grid item xs={12} md={4} key={item.title}>
                  <Box
                    sx={{
                      height: '100%',
                      borderRadius: '28px',
                      p: { xs: 3, md: 3.5 },
                      background: 'linear-gradient(180deg, #ffffff 0%, #f6fbff 100%)',
                      border: `1px solid ${alpha(palette.ink, 0.08)}`,
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 52,
                          height: 52,
                          borderRadius: '18px',
                          bgcolor: alpha(palette.heroBlue, 0.18),
                          color: palette.heroBlueMid,
                        }}
                      >
                        <Icon size={25} strokeWidth={1.9} />
                      </Box>
                      <Typography sx={{ fontWeight: 800, color: alpha(palette.ink, 0.36) }}>
                        {item.step}
                      </Typography>
                    </Stack>
                    <Typography
                      variant="h3"
                      sx={{
                        mb: 1.5,
                        fontFamily: headingFontFamily,
                        fontSize: '1.4rem',
                        lineHeight: 1.18,
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography sx={{ color: palette.inkSoft, lineHeight: 1.72 }}>{item.copy}</Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      <Box
        component="section"
        sx={{
          py: { xs: 7, md: 10 },
          background: 'linear-gradient(180deg, #ffffff 0%, #edf8fe 100%)',
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Box
            sx={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: { xs: '28px', md: '36px' },
              p: { xs: 3, sm: 4, md: 5 },
              background: 'linear-gradient(145deg, #123d67 0%, #1c6da1 100%)',
              color: palette.white,
              boxShadow: '0 34px 80px rgba(18, 61, 103, 0.18)',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 'auto auto -70px -50px',
                width: 180,
                height: 180,
                borderRadius: '50%',
                background: alpha('#ffffff', 0.12),
              }}
            />
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={8}>
                <Box
                  component="img"
                  src={marketingLogos.lockupLight}
                  alt="Virtual Medic"
                  sx={{
                    width: { xs: '100%', sm: 340 },
                    maxWidth: '100%',
                    mb: 3,
                  }}
                />
                <Typography
                  variant="h2"
                  sx={{
                    mb: 1.75,
                    fontFamily: headingFontFamily,
                    fontSize: { xs: '2rem', sm: '2.35rem', md: '2.8rem' },
                    lineHeight: 1.08,
                    letterSpacing: '-0.03em',
                  }}
                >
                  Unlimited consultation practice with feedback students can actually use.
                </Typography>
                <Typography
                  sx={{
                    maxWidth: 620,
                    color: alpha('#f9fcfb', 0.82),
                    fontSize: { xs: '1rem', md: '1.08rem' },
                    lineHeight: 1.72,
                  }}
                >
                  Virtual Medic is designed to bridge the gap between passive revision and speaking to
                  real patients. It gives learners a repeatable place to practise, reflect, and improve.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack spacing={1.5}>
                  <Button
                    component={Link}
                    to={isAuthenticated ? '/dashboard' : '/signup'}
                    endIcon={<ArrowRight size={18} />}
                    variant="contained"
                    sx={{
                      minHeight: 52,
                      bgcolor: palette.heroBlue,
                      color: palette.heroBlueDeep,
                      textTransform: 'none',
                      fontWeight: 800,
                      fontFamily: bodyFontFamily,
                      borderRadius: '999px',
                      boxShadow: 'none',
                      '&:hover': {
                        bgcolor: '#55c7ee',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {isAuthenticated ? 'Go to dashboard' : 'Create an account'}
                  </Button>
                  <Button
                    component={Link}
                    to="/about"
                    variant="outlined"
                    sx={{
                      minHeight: 52,
                      color: '#f9fcfb',
                      textTransform: 'none',
                      fontWeight: 700,
                      fontFamily: bodyFontFamily,
                      borderRadius: '999px',
                      borderColor: alpha('#ffffff', 0.22),
                      '&:hover': {
                        borderColor: alpha('#ffffff', 0.40),
                        bgcolor: alpha('#ffffff', 0.05),
                      },
                    }}
                  >
                    About Virtual Medic
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default HomePage;
